import dayjs from 'dayjs';
import { DataTableSortStatus, getValueAtPath } from 'mantine-datatable';
import { useMemo, useState } from 'react';

function useSortableRecords<T>(records: T[] | undefined) {
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>({
    columnAccessor: '' as string & keyof T,
    direction: 'asc',
  });

  const sortedRecords = useMemo(() => {
    if (!records) return undefined;
    if (!sortStatus.columnAccessor) return records;

    return [...records].sort((a, b) => {
      const valA = getValueAtPath(a, sortStatus.columnAccessor as string);
      const valB = getValueAtPath(b, sortStatus.columnAccessor as string);

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      let result: number;

      if (dayjs.isDayjs(valA) && dayjs.isDayjs(valB)) {
        result = valA.valueOf() - valB.valueOf();
      } else if (valA instanceof Date && valB instanceof Date) {
        result = valA.getTime() - valB.getTime();
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        result = valA - valB;
      } else {
        result = String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base' });
      }

      return sortStatus.direction === 'asc' ? result : -result;
    });
  }, [records, sortStatus]);

  return { sortedRecords, sortStatus, setSortStatus };
}

export default useSortableRecords;
